import {
  EmployerOwnership,
  Finding,
  FindingCode,
  FindingParams,
  ResultBasis,
  Step,
  StepCode,
} from 'api/StudentLoanApi';
import { formatDate } from 'utils/dateUtils';

/**
 * All borrower-facing wording for calculator findings and steps (v2.1). The API sends a code plus params only,
 * so copy changes ship from here.
 */

export interface StudentLoanCopy {
  title: string;
  body: string;
}

export interface StudentLoanCopyContext {
  basis?: ResultBasis;
  /** Whether the recommended path counts toward PSLF. */
  isPslfRecommended?: boolean;
}

type CopyBuilder = (params: FindingParams, context: StudentLoanCopyContext) => StudentLoanCopy;

const DEFAULT_SERVICER = 'your servicer';
const DEFAULT_ASSUMED_RATE_PERCENT = 6.5;
const DEFAULT_LOAN_SIMULATOR_URL = 'https://studentaid.gov/loan-simulator/';

const asString = (value: string | number | null | undefined, fallback: string): string =>
  value === null || value === undefined || value === '' ? fallback : String(value);

const asNumber = (value: string | number | null | undefined, fallback: number): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return value === null || value === undefined || Number.isNaN(parsed) ? fallback : parsed;
};

const formatFileDate = (isoDate: string | number | null | undefined): string => {
  if (typeof isoDate !== 'string') return 'an unknown date';
  const date = new Date(isoDate);
  return Number.isNaN(date.getTime()) ? 'an unknown date' : formatDate(date);
};

const formatRatePercent = (ratePct: string | number | null | undefined): string =>
  `${asNumber(ratePct, DEFAULT_ASSUMED_RATE_PERCENT).toFixed(1)}%`;

const findingCopy: Record<FindingCode, CopyBuilder> = {
  [FindingCode.Default]: () => ({
    title: 'Some of your loans are in default.',
    body: "Defaulted loans can't earn PSLF credit or use a repayment plan until they're out of default.",
  }),
  [FindingCode.PlanNotPslfQualifying]: (params) => ({
    title: "Your plan doesn't count toward PSLF.",
    body: `${asString(
      params.plan,
      'Your current plan',
    )} payments never count, which is why your PSLF count is ${asNumber(
      params.pslfMonths,
      0,
    )}. Switching plans starts your count.`,
  }),
  [FindingCode.SaveEnding]: () => ({
    title: 'SAVE is ending.',
    body:
      "If you don't choose a new plan within 90 days of your servicer's notice, you'll be moved to the Standard plan.",
  }),
  [FindingCode.ConfirmCurrentPlan]: () => ({
    title: 'Confirm your current plan.',
    body: 'Your file predates the SAVE transition. If your servicer already moved you, download a fresh file.',
  }),
  [FindingCode.PossiblePastDue]: (params) => ({
    title: 'Your file may show a missed payment.',
    body: `Check with ${asString(params.servicer, DEFAULT_SERVICER)} that your account is current.`,
  }),
  [FindingCode.StaleFile]: (params) => ({
    title: `Your file is from ${formatFileDate(params.fileDate)}.`,
    body: 'Download a new one so we can turn this range into a plan.',
  }),
  [FindingCode.FileWarnings]: () => ({
    title: 'Parts of your file were incomplete.',
    body: 'We filled the gaps with assumptions. A fresh download may fix this.',
  }),
  [FindingCode.FfelOrPerkins]: () => ({
    title: 'Some of your loans are FFEL or Perkins loans.',
    body:
      'They need a Direct Consolidation Loan for PSLF and RAP (Perkins also for IBR). Consolidate only those loans so your Direct Loans keep their PSLF counts.',
  }),
  [FindingCode.ParentPlusNotModeled]: () => ({
    title: 'You have Parent PLUS loans.',
    body:
      'Their options depend on when and how they were consolidated. We left them on their current plan; check them with the official Loan Simulator.',
  }),
  [FindingCode.ConsolidationLineageUnknown]: () => ({
    title: 'Confirm what your consolidation loan includes.',
    body: 'If it repaid any Parent PLUS loans, its options are limited and this plan may not apply.',
  }),
  [FindingCode.PslfReady]: () => ({
    title: 'You may already have 120 PSLF payments.',
    body: 'Apply for forgiveness in the PSLF Help Tool on studentaid.gov.',
  }),
  [FindingCode.CurrentPaymentUnknown]: () => ({
    title: "We couldn't see a regular payment.",
    body:
      'Your payment is missing, below the monthly interest, or too low to pay off within 30 years, so we compared against the Standard plan.',
  }),
  [FindingCode.NotInRepayment]: () => ({
    title: "Some loans aren't in repayment yet.",
    body: 'We assumed repayment starts now on the Standard plan.',
  }),
  [FindingCode.PaymentsPaused]: () => ({
    title: 'Some payments are paused.',
    body: "Months in deferment or forbearance generally don't count toward PSLF. We assumed payments resume now.",
  }),
  [FindingCode.IdrProgressApproximate]: () => ({
    title: 'Your income-driven progress is approximate.',
    body: 'Your file has no forgiveness count, so we counted months since your plan started.',
  }),
  [FindingCode.ScheduleApproximate]: () => ({
    title: 'Your graduated schedule is approximate.',
    body: 'Your file has no plan start date, so we estimated when your payments step up.',
  }),
  [FindingCode.NotPaidOff]: () => ({
    title: 'A payment schedule never pays off.',
    body:
      "At your current payment, some loans aren't paid off within 50 years. Your servicer can confirm your real schedule.",
  }),
  [FindingCode.RateAssumed]: (params, { basis }) =>
    basis === undefined || basis === ResultBasis.CreditReport
      ? {
          title: 'We assumed an interest rate.',
          body: `Credit reports don't show rates, so we used ${formatRatePercent(params.ratePct)}.`,
        }
      : {
          title: 'Some interest rates were missing.',
          body: `We used ${formatRatePercent(params.ratePct)} where the file had no rate.`,
        },
  [FindingCode.LoanProgramUnverified]: () => ({
    title: "We can't confirm your loan types yet.",
    body:
      'Some loans may be older FFEL or Perkins loans, which need consolidation for PSLF. Your studentaid.gov file confirms this.',
  }),
};

export const getFindingCopy = ({ code, params }: Finding, context: StudentLoanCopyContext = {}): StudentLoanCopy =>
  findingCopy[code](params ?? {}, context);

const stepCopy: Record<StepCode, CopyBuilder> = {
  [StepCode.UploadNewFile]: () => ({
    title: 'Upload a new studentaid.gov file',
    body: "We couldn't read this file. Download My Aid Data again from studentaid.gov and upload the new file.",
  }),
  [StepCode.GetOutOfDefault]: () => ({
    title: 'Get your loans out of default',
    body:
      "Contact the Department of Education's Default Resolution Group about loan rehabilitation or consolidation. Then come back to build a repayment plan.",
  }),
  [StepCode.DownloadFreshFile]: () => ({
    title: 'Download a fresh studentaid.gov file',
    body: 'Your file is too old to build a plan from. Download My Aid Data again and upload it.',
  }),
  [StepCode.ConfirmAccountCurrent]: (params) => ({
    title: 'Confirm your account is current',
    body: `Check with ${asString(
      params.servicer,
      DEFAULT_SERVICER,
    )}. Ask about a lower plan or forbearance if you can't pay.`,
  }),
  [StepCode.ApplyPslfNow]: () => ({
    title: 'Apply for PSLF forgiveness now',
    body: 'Use the PSLF Help Tool on studentaid.gov for the loans that have 120 qualifying payments.',
  }),
  [StepCode.PickNewPlanBeforeDeadline]: () => ({
    title: 'Pick a new plan before your deadline',
    body: "Choose within 90 days of your servicer's notice.",
  }),
  [StepCode.ConsolidateFfelPerkins]: () => ({
    title: 'Consolidate only your FFEL or Perkins loans',
    body:
      'Apply for a Direct Consolidation Loan on studentaid.gov and leave your Direct Loans out, so their PSLF counts are not averaged down. The new loan uses RAP.',
  }),
  [StepCode.StayOnCurrentPlan]: () => ({
    title: 'Stay on your current plan',
    body: 'It is already your best option based on what we know.',
  }),
  [StepCode.SwitchPlan]: (params) => {
    const from = asString(params.from, '');
    const to = asString(params.to, 'a new plan');
    return {
      title: from ? `Switch from ${from} to ${to}` : `Switch to ${to}`,
      body: `Estimated payment: $${asNumber(params.monthlyPayment, 0).toLocaleString(
        'en-US',
      )} a month to start. Apply on studentaid.gov and let it pull your tax info from the IRS.`,
    };
  },
  [StepCode.CertifyEmployer]: () => ({
    title: 'Certify your employer for PSLF',
    body: "Use the PSLF Help Tool on studentaid.gov. You'll need your employer's EIN (box b of your W-2).",
  }),
  [StepCode.RestartPausedPayments]: () => ({
    title: 'Restart paused payments',
    body: "Months in deferment or forbearance generally don't count toward PSLF.",
  }),
  [StepCode.CheckParentPlus]: (params) => ({
    title: 'Check your Parent PLUS options',
    body: `Use the official Loan Simulator: ${asString(params.url, DEFAULT_LOAN_SIMULATOR_URL)}`,
  }),
  [StepCode.RecertifyYearly]: (_params, { isPslfRecommended }) => ({
    title: 'Recertify every year',
    body: isPslfRecommended
      ? 'Update your income each year and certify your employment when you change jobs.'
      : 'Update your income each year so your payment stays accurate.',
  }),
};

export const getStepCopy = ({ code, params }: Step, context: StudentLoanCopyContext = {}): StudentLoanCopy =>
  stepCopy[code](params ?? {}, context);

const ownershipLabels: Record<EmployerOwnership, string> = {
  [EmployerOwnership.Nonprofit]: 'nonprofit',
  [EmployerOwnership.Government]: 'government',
  [EmployerOwnership.ForProfit]: 'for-profit',
  [EmployerOwnership.Unknown]: 'unknown',
};

const ownershipNotes: Record<EmployerOwnership, string> = {
  [EmployerOwnership.Nonprofit]: 'Full-time work there can count toward PSLF.',
  [EmployerOwnership.Government]: 'Full-time work there can count toward PSLF.',
  [EmployerOwnership.ForProfit]:
    "Work there doesn't count toward PSLF, but an income-driven plan can still lower your payment.",
  [EmployerOwnership.Unknown]: "We'll treat PSLF as unconfirmed until you check your employer.",
};

export const getHospitalOwnershipCopy = (hospitalName: string, ownership: EmployerOwnership): StudentLoanCopy => ({
  title:
    ownership === EmployerOwnership.Unknown
      ? `We couldn't find ${hospitalName} in hospital ownership records.`
      : `${hospitalName} is a ${ownershipLabels[ownership]} employer in CMS hospital ownership records.`,
  body: ownershipNotes[ownership],
});

/** CMS lists facility names in capitals. */
export const formatHospitalName = (name: string): string =>
  name === name.toUpperCase()
    ? name
        .toLowerCase()
        .replace(/(^|[\s-])([a-z])/g, (_match, separator: string, letter: string) => separator + letter.toUpperCase())
        .replace(/\bOf\b/g, 'of')
        .replace(/\bAnd\b/g, 'and')
    : name;
